/* Consented device observations. Account identity is exclusively server-proven. */
(function (window, document) {
  'use strict';
  if (window.MissioIntelligence) return;
  var config = window.MissioIntelligenceConfig || {};
  var base = config.endpoint;
  if (!base || !window.fetch || !window.crypto || !window.crypto.randomUUID) return;
  var nativeFetch = window.fetch.bind(window);
  var key = '_pcbd_ci_v1';
  var sessionKey = '_pcbd_ci_session_v1';
  var revokePrefix = '_pcbd_ci_revoke_v1:';
  var subject = null, session = null, allowed = false, generation = 0;
  var queue = [], opening = null, sending = false, pendingRevokes = new Set();
  var isolateRejected = false, rejectedCount = 0;
  var linkControl = null, linkState = 'idle', linkExpiresAt = 0, userPaused = false;
  var lastTick = Date.now(), visible = 0, active = 0, pageView = null;
  var wasVisible = document.visibilityState === 'visible', wasFocused = document.hasFocus();
  function uuid() { return window.crypto.randomUUID(); }
  function validSubject(value) {
    return value && typeof value.capability === 'string' && value.capability.length > 0 &&
      value.visitor_id && value.identity_epoch && Number.isFinite(Date.parse(value.expires_at)) && Date.parse(value.expires_at) > Date.now();
  }
  function permission() {
    try { return window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  }
  function storage(kind, op, name, value) {
    try { return window[kind][op](name, value); } catch (_) { return null; }
  }
  function request(path, body, capability) {
    var headers = { 'Content-Type': 'application/json' };
    if (capability) headers['X-Intelligence-Capability'] = capability;
    return nativeFetch(base + path, { method: 'POST', headers: headers,
      body: JSON.stringify(body || {}), keepalive: true, credentials: 'omit' });
  }
  // These strictly-purpose revocation obligations are never analytics identity.
  // One key per capability avoids overwriting another tab's pending withdrawal.
  function rememberRevoke(cap) {
    pendingRevokes.add(cap);
    storage('localStorage', 'setItem', revokePrefix + cap, 'pending');
  }
  function recoverRevokes() {
    try {
      for (var i = 0; i < window.localStorage.length; i++) {
        var name = window.localStorage.key(i);
        if (name && name.indexOf(revokePrefix) === 0) pendingRevokes.add(name.slice(revokePrefix.length));
      }
    } catch (_) { /* Storage denied: only in-memory retry is possible. */ }
  }
  async function retryRevokes() {
    await Promise.all(Array.from(pendingRevokes).map(async function (cap) {
      try {
        var response = await request('/revoke', {}, cap);
        if (!response.ok) return;
        var receipt = await response.json();
        if (!['pending_warehouse', 'erased'].includes(receipt.erasure_state) || !Number.isInteger(receipt.authority_version)) return;
        pendingRevokes.delete(cap);
        storage('localStorage', 'removeItem', revokePrefix + cap);
      } catch (_) { /* Keep persisted withdrawal obligation until durable ACK. */ }
    }));
    renderLinkControl();
  }
  function revoke() {
    allowed = false; generation += 1; queue = []; pageView = null;
    if (subject) rememberRevoke(subject.capability);
    subject = null; session = null; linkExpiresAt = 0;
    if (linkState === 'linked' || linkState === 'pending') { linkState = 'withdrawn'; userPaused = true; }
    renderLinkControl();
    storage('localStorage', 'removeItem', key);
    storage('sessionStorage', 'removeItem', sessionKey);
    retryRevokes();
  }
  async function consentChanged() {
    renderLinkControl();
    if (userPaused) return;
    if (!permission()) {
      if (allowed || subject || opening) revoke();
      else {
        // Consent may have been withdrawn while this page was not running.
        // Read the old capability only to withdraw it, never to resume tracking.
        try {
          var old = JSON.parse(storage('localStorage', 'getItem', key) || 'null');
          if (old && typeof old.capability === 'string') { rememberRevoke(old.capability); storage('localStorage', 'removeItem', key); storage('sessionStorage', 'removeItem', sessionKey); retryRevokes(); }
        } catch (_) { storage('localStorage', 'removeItem', key); }
      }
      return;
    }
    if (allowed && validSubject(subject)) return;
    if (opening) return opening;
    if (subject) { rememberRevoke(subject.capability); retryRevokes(); generation += 1; queue = []; subject = null; session = null; }
    allowed = true;
    var version = generation;
    opening = (async function () {
      var candidate;
      try {
        try { candidate = JSON.parse(storage('localStorage', 'getItem', key) || 'null'); } catch (_) { candidate = null; }
        if (candidate && candidate.linked_page) { rememberRevoke(candidate.capability); candidate = null; retryRevokes(); }
        if (candidate && pendingRevokes.has(candidate.capability)) candidate = null;
        if (!validSubject(candidate)) {
          if (candidate && typeof candidate.capability === 'string') { rememberRevoke(candidate.capability); retryRevokes(); }
          if (storage('localStorage', 'setItem', '_pcbd_ci_storage_probe_v1', '1') === null) return;
          storage('localStorage', 'removeItem', '_pcbd_ci_storage_probe_v1');
          var response = await request('/session', { schema_version: 1, source: 'storefront',
            consent: { analytics: true, customer_linking: false, policy_version: 'pcbd-ci-v1' } });
          if (!response.ok) return;
          candidate = await response.json();
        }
        if (!validSubject(candidate)) return;
        if (!permission() || version !== generation) {
          rememberRevoke(candidate.capability); retryRevokes(); return;
        }
        subject = candidate;
        session = storage('sessionStorage', 'getItem', sessionKey);
        if (!/^[0-9a-f-]{36}$/i.test(session || '')) session = uuid();
        if (storage('localStorage', 'setItem', key, JSON.stringify(candidate)) === null) {
          rememberRevoke(candidate.capability); subject = null; session = null; retryRevokes(); return;
        }
        storage('sessionStorage', 'setItem', sessionKey, session);
        pageView = uuid(); visible = 0; active = 0; lastTick = Date.now();
        emit('page_viewed', { page_type: config.page_type || 'other' });
        if (config.product_id) {
          var product = { product_id: String(config.product_id) };
          if (config.variant_id) product.variant_id = String(config.variant_id);
          emit('product_viewed', product);
        }
        if (config.collection_id) emit('collection_viewed', { collection_id: String(config.collection_id) });
        if (config.page_type === 'cart') confirmCart();
      } catch (_) { /* Unavailable API/storage cannot affect commerce. */ }
    })();
    try { await opening; } finally { opening = null; renderLinkControl(); }
  }
  function capability() {
    if (!allowed || !permission()) { if (allowed) revoke(); return null; }
    if (!subject || Date.parse(subject.expires_at) <= Date.now()) return null;
    return subject.capability;
  }
  function emit(type, properties) {
    if (!capability() || queue.length >= 120) return false;
    queue.push({ schema_version: 1, event_id: uuid(), session_id: session,
      occurred_at: new Date().toISOString(), page_view_id: pageView, type: type, properties: properties });
    flush(); return true;
  }
  async function flush() {
    var cap = capability();
    if (!cap || sending || !queue.length) return;
    sending = true;
    var batch = queue.slice(0, isolateRejected ? 1 : 12), version = generation;
    try {
      var response = await request('/events', { events: batch }, cap);
      if (response.status === 401) { revoke(); return; }
      if (response.status === 422 || response.status === 413) {
        if (version !== generation) return;
        if (batch.length > 1) isolateRejected = true;
        else { queue = queue.filter(function (e) { return e.event_id !== batch[0].event_id; }); rejectedCount += 1; }
        return;
      }
      if (!response.ok) return;
      var receipt = await response.json();
      if (version !== generation || receipt.ingress_state !== 'durable' || !Array.isArray(receipt.accepted)) return;
      var sent = new Set(batch.map(function (e) { return e.event_id; }));
      var accepted = new Set(receipt.accepted.filter(function (id) { return sent.has(id); }));
      (receipt.conflicts || []).forEach(function (id) { if (sent.has(id) && !accepted.has(id)) { accepted.add(id); rejectedCount += 1; } });
      queue = queue.filter(function (e) { return !accepted.has(e.event_id); });
      if (!queue.length) isolateRejected = false;
    } catch (_) { /* Lost ACK preserves exact IDs and bodies for retry. */ }
    finally { sending = false; }
  }
  async function confirmCart() {
    if (!capability()) return;
    var version = generation;
    try {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
      var response = await nativeFetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return;
      var cart = await response.json();
      if (version !== generation || !Array.isArray(cart.items) || cart.items.length > 20) return;
      var items = cart.items.map(function (item) {
        return { product_id: String(item.product_id), variant_id: String(item.variant_id), quantity: item.quantity };
      });
      emit('cart_snapshot', { items: items, currency: cart.currency, amount: (cart.total_price / 100).toFixed(2) });
    } catch (_) { /* No successful cart response means no cart observation. */ }
  }
  function mutation(input, options) {
    try {
      var url = new URL(typeof input === 'string' ? input : input.url, window.location.href);
      var method = String(options && options.method || input.method || 'GET').toUpperCase();
      return url.origin === window.location.origin && method === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    } catch (_) { return false; }
  }
  var previousFetch = window.fetch;
  function logout(input) {
    try { var url = new URL(typeof input === 'string' ? input : input.url, window.location.href);
      return url.origin === window.location.origin && /\/account\/logout\/?$/.test(url.pathname);
    } catch (_) { return false; }
  }
  window.fetch = function (input, options) {
    if (subject && subject.linked_page && logout(input)) revoke();
    var result = previousFetch.apply(this, arguments);
    if (mutation(input, options)) result.then(function (response) { if (response.ok) confirmCart(); }, function () {});
    return result;
  };
  if (window.XMLHttpRequest) {
    var prototype = window.XMLHttpRequest.prototype, previousOpen = prototype.open, previousSend = prototype.send;
    var xhrRequests = new WeakMap();
    prototype.open = function (method, url) { xhrRequests.set(this, { method: method, url: url }); return previousOpen.apply(this, arguments); };
    prototype.send = function () {
      var self = this, info = xhrRequests.get(this);
      if (info && mutation(info.url, { method: info.method })) this.addEventListener('load', function () { if (self.status >= 200 && self.status < 300) confirmCart(); }, { once: true });
      return previousSend.apply(this, arguments);
    };
  }
  function checkpoint() {
    var now = Date.now(), elapsed = Math.max(0, Math.min(now - lastTick, 60000)); lastTick = now;
    var hadVisibility = wasVisible, hadFocus = wasFocused;
    wasVisible = document.visibilityState === 'visible'; wasFocused = document.hasFocus();
    if (!capability()) return;
    if (hadVisibility) {
      visible = Math.min(86400000, visible + elapsed);
      if (hadFocus) active = Math.min(visible, active + elapsed);
    }
    emit('exposure', { content_id: 'page', visible_ms: visible, active_ms: active });
  }
  async function grantCustomerLink(consent) {
    if (consent !== true || !capability() || !config.identity_proxy_path) return { available: false };
    var version = generation;
    try {
    var response = await request('/identity/challenge', { purpose: 'customer_linking', policy_version: 'pcbd-ci-v1', consent: true }, capability());
    if (!response.ok || version !== generation || !capability()) return { available: false };
    var challenge = await response.json();
    if (typeof challenge.nonce !== 'string' || !challenge.nonce) return { available: false };
    var url = new URL(config.identity_proxy_path, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.startsWith('/apps/')) return { available: false };
    url.searchParams.set('nonce', challenge.nonce); url.searchParams.set('purpose', 'customer_linking'); url.searchParams.set('policy_version', 'pcbd-ci-v1'); url.searchParams.set('consent', 'true');
    // Mark before dispatch: a lost grant ACK must still revoke on navigation.
    subject.linked_page = pageView;
    if (storage('localStorage', 'setItem', key, JSON.stringify(subject)) === null) return { available: false };
    var result = await nativeFetch(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { 'X-Intelligence-Capability': capability() } });
    if (!result.ok) return { available: false };
    var grant = await result.json();
    var valid = /^[0-9a-f-]{36}$/i.test(grant.grant_id || '') &&
      Date.parse(grant.expires_at) > Date.now() && version === generation && !!capability();
    if (valid) { subject.linked_page = pageView; storage('localStorage', 'setItem', key, JSON.stringify(subject)); }
    return { available: valid, expires_at: valid ? grant.expires_at : null };
    } catch (_) { return { available: false }; }
  }
  function renderLinkControl() {
    if (!linkControl) return;
    if (linkState === 'linked' && (Date.now() >= linkExpiresAt || !permission() || !subject)) {
      linkState = 'withdrawn'; userPaused = true; revoke();
    }
    var messages = {
      idle: 'Opcional: vincula la actividad futura de esta página a tu cuenta para comprender cómo usas la tienda. No se vincula la navegación anterior. El vínculo termina al salir de esta página o al caducar. Puedes seguir comprando y usar el chat sin vincularla.',
      pending: 'Comprobando tu consentimiento y tu cuenta…',
      linked: 'Vínculo activo para la actividad futura de esta página. Puedes retirarlo en cualquier momento.',
      unavailable: 'No se pudo verificar el vínculo. Comprueba que has iniciado sesión. Puedes seguir comprando y usar el chat.',
      withdrawn: pendingRevokes.size ? 'Actividad detenida en esta página. Retirada pendiente de confirmar; se reintentará cuando haya conexión.' : 'Retirada confirmada. La actividad permanece detenida en esta página. El borrado físico, si corresponde, se procesa por separado.'
    };
    var message = messages[linkState];
    if (linkState === 'idle' && !config.identity_proxy_path) message = 'El vínculo con tu cuenta todavía no está disponible. Puedes seguir comprando y usar el chat.';
    else if (linkState === 'idle' && !permission()) message += ' Requiere permitir analítica en las preferencias de privacidad de la tienda.';
    linkControl.status.textContent = message;
    linkControl.button.disabled = linkState === 'pending' || linkState === 'linked' || !config.identity_proxy_path || !permission();
    linkControl.button.textContent = linkState === 'pending' ? 'Verificando…' : 'Acepto vincular esta página a mi cuenta';
    linkControl.withdraw.disabled = !(subject || pendingRevokes.size || linkState === 'pending');
    linkControl.withdraw.textContent = pendingRevokes.size && !subject ? 'Reintentar retirada' : 'Retirar y detener actividad en esta página';
  }
  function mountLinkControl() {
    if (config.linking_control !== true || linkControl || !document.createElement || !document.body) return;
    var panel = document.createElement('details');
    panel.id = 'pcbd-intelligence-consent';
    panel.style.cssText = 'padding:16px;margin:16px;border:1px solid currentColor;border-radius:6px;font:inherit;';
    var title = document.createElement('summary'); title.textContent = 'Actividad de esta página y tu cuenta';
    var status = document.createElement('p'); status.id = 'pcbd-intelligence-consent-status'; status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    var button = document.createElement('button'); button.type = 'button'; button.setAttribute('aria-describedby', status.id);
    var withdraw = document.createElement('button'); withdraw.type = 'button'; withdraw.style.margin = '8px';
    panel.appendChild(title); panel.appendChild(status); panel.appendChild(button); panel.appendChild(withdraw);
    linkControl = { panel: panel, status: status, button: button, withdraw: withdraw };
    button.addEventListener('click', async function () {
      if (button.disabled) return;
      userPaused = false; linkState = 'pending'; renderLinkControl();
      await consentChanged();
      var attemptGeneration = generation;
      var result = await grantCustomerLink(true);
      if (attemptGeneration !== generation || userPaused) { renderLinkControl(); return; }
      if (result.available) { linkState = 'linked'; linkExpiresAt = Date.parse(result.expires_at); }
      else { userPaused = true; revoke(); linkState = 'unavailable'; }
      renderLinkControl();
    });
    withdraw.addEventListener('click', function () {
      userPaused = true; linkState = 'withdrawn'; revoke(); renderLinkControl();
    });
    var footer = document.querySelector && document.querySelector('footer');
    (footer || document.body).appendChild(panel); renderLinkControl();
  }
  window.MissioIntelligence = { getCapability: capability, emit: emit, flush: flush,
    revoke: revoke, grantCustomerLink: grantCustomerLink, confirmCart: confirmCart,
    getTransportState: function () { return { pending: queue.length, rejected: rejectedCount }; } };
  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('visibilitychange', checkpoint);
  window.addEventListener('focus', checkpoint);
  window.addEventListener('blur', checkpoint);
  document.addEventListener('click', function (event) { var link = event.target.closest && event.target.closest('a[href]'); if (link && subject && subject.linked_page && logout(link.href)) revoke(); }, true);
  document.addEventListener('submit', function (event) { if (subject && subject.linked_page && logout(event.target.action || '')) revoke(); }, true);
  window.addEventListener('pagehide', function () { if (subject && subject.linked_page) revoke(); else { checkpoint(); flush(); } });
  window.addEventListener('online', function () { recoverRevokes(); retryRevokes(); flush(); });
  window.setInterval(function () { consentChanged(); retryRevokes(); flush(); }, 5000);
  window.setInterval(checkpoint, 15000);
  if (window.Shopify && window.Shopify.loadFeatures && !window.Shopify.customerPrivacy) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) { if (!error) consentChanged(); });
  }
  mountLinkControl();
  if (!linkControl) document.addEventListener('DOMContentLoaded', mountLinkControl);
  recoverRevokes(); retryRevokes(); consentChanged();
})(window, document);
